import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useDepositos } from '@/features/productos/hooks'
import AjusteStockModal from '@/features/productos/AjusteStockModal'
import type { Producto, Deposito } from '@/types/database'
import { SearchIcon } from '@/components/shared/Icons'

function useStockCompleto() {
  return useQuery({
    queryKey: ['stock-page'],
    queryFn: () => api.get<Producto[]>('/api/stock/pagina'),
    refetchInterval: 30_000,
  })
}

function useMovimientos(limite = 50) {
  return useQuery({
    queryKey: ['movimientos-recientes', limite],
    queryFn: () => api.get<unknown[]>(`/api/stock/movimientos?limit=${limite}`),
  })
}

export default function StockPage() {
  const { data: productos = [], isLoading } = useStockCompleto()
  const { data: depositos = [] } = useDepositos()
  const { data: movimientos = [] } = useMovimientos()

  const [tab, setTab] = useState<'stock' | 'movimientos'>('stock')
  const [busqueda, setBusqueda] = useState('')
  const [filtro, setFiltro] = useState<'todos' | 'sin' | 'bajo'>('todos')
  const [ajustando, setAjustando] = useState<Producto | null>(null)

  const filtrados = useMemo(() => {
    return productos.filter(p => {
      const stockTotal = (p.stock ?? []).reduce((s, r) => s + r.cantidad_actual, 0)
      const matchBusqueda = !busqueda || (
        p.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
        p.marca?.toLowerCase().includes(busqueda.toLowerCase()) ||
        p.codigo_barras?.includes(busqueda)
      )
      const matchFiltro =
        filtro === 'todos' ? true :
        filtro === 'sin' ? stockTotal === 0 :
        stockTotal > 0 && stockTotal < p.stock_minimo
      return matchBusqueda && matchFiltro
    })
  }, [productos, busqueda, filtro])

  const sinStock = productos.filter(p => (p.stock ?? []).reduce((s, r) => s + r.cantidad_actual, 0) === 0).length
  const stockBajo = productos.filter(p => {
    const t = (p.stock ?? []).reduce((s, r) => s + r.cantidad_actual, 0)
    return t > 0 && t < p.stock_minimo
  }).length

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div className="me-eyebrow" style={{ marginBottom: 6 }}>Inventario</div>
          <h1 className="me-page-title">Stock</h1>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <KpiChip label="Productos" value={productos.length} />
        <KpiChip
          label="Sin stock"
          value={sinStock}
          color="var(--me-bad)"
          onClick={() => setFiltro(f => f === 'sin' ? 'todos' : 'sin')}
          active={filtro === 'sin'}
        />
        <KpiChip
          label="Stock bajo"
          value={stockBajo}
          color="var(--me-warn)"
          onClick={() => setFiltro(f => f === 'bajo' ? 'todos' : 'bajo')}
          active={filtro === 'bajo'}
        />
        {depositos.map(d => {
          const total = productos.reduce((s, p) => {
            const row = (p.stock ?? []).find(r => (r.depositos as unknown as Deposito)?.id === d.id)
            return s + (row?.cantidad_actual ?? 0)
          }, 0)
          return <KpiChip key={d.id} label={d.nombre} value={total} />
        })}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 16, borderBottom: '1px solid var(--me-line)' }}>
        {(['stock', 'movimientos'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '8px 16px',
              fontSize: 13.5, fontWeight: 500,
              color: tab === t ? 'var(--me-text)' : 'var(--me-text-3)',
              borderBottom: `2px solid ${tab === t ? 'var(--me-acid)' : 'transparent'}`,
              marginBottom: -1,
              transition: 'color var(--me-dur-short)',
            }}
          >
            {t === 'stock' ? 'Stock actual' : 'Movimientos'}
          </button>
        ))}
      </div>

      {tab === 'stock' ? (
        <>
          {/* Filtros */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 320 }}>
              <SearchIcon
                size={15}
                style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--me-text-3)' }}
              />
              <input
                className="me-input"
                style={{ width: '100%', paddingLeft: 34 }}
                type="text"
                placeholder="Buscar producto..."
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
              />
            </div>
          </div>

          {isLoading ? (
            <Skeleton />
          ) : filtrados.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--me-text-3)' }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Sin resultados</div>
              <div style={{ fontSize: 13 }}>
                {busqueda ? `No hay productos que coincidan con "${busqueda}"` : 'Ajusta los filtros para ver productos'}
              </div>
            </div>
          ) : (
            <div className="me-card" style={{ overflow: 'hidden', padding: 0 }}>
              <table className="me-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Producto</th>
                    {depositos.map(d => (
                      <th key={d.id} style={{ textAlign: 'right' }}>{d.nombre}</th>
                    ))}
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th style={{ textAlign: 'center' }}>Estado</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filtrados.map(p => {
                    const stockTotal = (p.stock ?? []).reduce((s, r) => s + r.cantidad_actual, 0)
                    const status = stockTotal === 0 ? 'sin' : stockTotal < p.stock_minimo ? 'bajo' : 'ok'
                    return (
                      <tr key={p.id}>
                        <td>
                          <div style={{ fontWeight: 500, fontSize: 13.5 }}>{p.nombre}</div>
                          {p.marca && <div style={{ fontSize: 12, color: 'var(--me-text-3)' }}>{p.marca}</div>}
                        </td>
                        {depositos.map(d => {
                          const row = (p.stock ?? []).find(r => (r.depositos as unknown as Deposito)?.id === d.id)
                          return (
                            <td key={d.id} style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontSize: 13 }}>
                              {row?.cantidad_actual ?? 0}
                            </td>
                          )
                        })}
                        <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontSize: 13, fontWeight: 700 }}>
                          {stockTotal}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <StockBadge status={status} minimo={p.stock_minimo} />
                        </td>
                        <td>
                          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => setAjustando(p)}
                              className="me-btn me-btn-ghost"
                              style={{ fontSize: 11, padding: '4px 10px' }}
                            >
                              Ajustar
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <MovimientosTab movimientos={movimientos} />
      )}

      {ajustando && depositos.length > 0 && (
        <AjusteStockModal
          producto={ajustando}
          depositos={depositos}
          onClose={() => setAjustando(null)}
        />
      )}
    </div>
  )
}

function MovimientosTab({ movimientos }: { movimientos: unknown[] }) {
  const TIPO_LABEL: Record<string, string> = {
    entrada: 'Entrada',
    salida: 'Salida',
    ajuste: 'Ajuste',
    devolucion: 'Devolucion',
    merma: 'Merma',
  }
  const TIPO_COLOR: Record<string, { color: string; bg: string }> = {
    entrada:    { color: 'var(--me-ok)',   bg: 'var(--me-ok-soft)' },
    salida:     { color: 'var(--me-bad)',  bg: 'var(--me-bad-soft)' },
    ajuste:     { color: '#4a5800',        bg: 'rgba(201,240,0,0.12)' },
    devolucion: { color: 'var(--me-mayor)', bg: 'var(--me-mayor-soft)' },
    merma:      { color: 'var(--me-warn)', bg: 'var(--me-warn-soft)' },
  }

  if (movimientos.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--me-text-3)' }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Sin movimientos registrados</div>
        <div style={{ fontSize: 13 }}>El historial aparece aqui cuando ajustes stock</div>
      </div>
    )
  }

  return (
    <div className="me-card" style={{ overflow: 'hidden', padding: 0 }}>
      <table className="me-table" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th>Fecha</th>
            <th>Producto</th>
            <th>Deposito</th>
            <th style={{ textAlign: 'center' }}>Tipo</th>
            <th style={{ textAlign: 'right' }}>Cantidad</th>
            <th>Notas</th>
          </tr>
        </thead>
        <tbody>
          {(movimientos as Array<Record<string, unknown>>).map((m) => {
            const fecha = new Date(m.created_at as string)
            const tipo = m.tipo as string
            const prod = m.producto as { nombre: string } | null
            const dep = m.deposito as { nombre: string } | null
            const tipoStyle = TIPO_COLOR[tipo] ?? { color: 'var(--me-text-3)', bg: 'var(--me-surface-2)' }
            return (
              <tr key={m.id as string}>
                <td>
                  <div style={{ fontSize: 12.5, fontFamily: 'var(--me-mono)', color: 'var(--me-text-2)' }}>
                    {fecha.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--me-text-4)', fontFamily: 'var(--me-mono)' }}>
                    {fecha.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </td>
                <td style={{ fontWeight: 500, fontSize: 13.5 }}>{prod?.nombre ?? '—'}</td>
                <td style={{ fontSize: 12.5, color: 'var(--me-text-3)' }}>{dep?.nombre ?? '—'}</td>
                <td style={{ textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 999,
                    fontSize: 11, fontWeight: 600,
                    color: tipoStyle.color,
                    background: tipoStyle.bg,
                  }}>
                    {TIPO_LABEL[tipo] ?? tipo}
                  </span>
                </td>
                <td style={{
                  textAlign: 'right', fontFamily: 'var(--me-mono)', fontSize: 13, fontWeight: 600,
                  color: tipo === 'entrada' ? 'var(--me-ok)' : tipo === 'salida' ? 'var(--me-bad)' : 'var(--me-text)',
                }}>
                  {tipo === 'entrada' ? '+' : tipo === 'salida' ? '-' : ''}{Math.abs(m.cantidad as number)}
                </td>
                <td style={{ fontSize: 12.5, color: 'var(--me-text-3)' }}>
                  {(m.notas as string | null) ?? '—'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function StockBadge({ status, minimo }: { status: 'ok' | 'bajo' | 'sin'; minimo: number }) {
  const map = {
    ok:   { label: 'OK',                   color: 'var(--me-ok)',   bg: 'var(--me-ok-soft)' },
    bajo: { label: `Bajo (min ${minimo})`, color: 'var(--me-warn)', bg: 'var(--me-warn-soft)' },
    sin:  { label: 'Sin stock',            color: 'var(--me-bad)',  bg: 'var(--me-bad-soft)' },
  }
  const s = map[status]
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
      color: s.color, background: s.bg,
    }}>
      {s.label}
    </span>
  )
}

function KpiChip({ label, value, color, onClick, active }: {
  label: string; value: number; color?: string; onClick?: () => void; active?: boolean
}) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '5px 12px',
        background: active ? 'var(--me-surface-3)' : 'var(--me-surface)',
        border: `1px solid ${active ? 'var(--me-line)' : 'var(--me-line-2)'}`,
        borderRadius: 8, fontSize: 12,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background var(--me-dur-short)',
      }}
    >
      <span style={{ color: 'var(--me-text-3)' }}>{label}</span>
      <span style={{ fontWeight: 700, fontFamily: 'var(--me-mono)', color: color ?? 'var(--me-text)' }}>
        {value}
      </span>
    </div>
  )
}

function Skeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {[...Array(8)].map((_, i) => (
        <div key={i} style={{ height: 48, background: 'var(--me-surface-3)', borderRadius: 8, opacity: 1 - i * 0.1 }} />
      ))}
    </div>
  )
}
